/**
 * Conflict: iemand anders heeft deze website ondertussen gewijzigd.
 *
 * Er is bewust geen "gewoon opslaan"-knop die stilzwijgend wint. De twee
 * uitkomsten zijn allebei expliciet benoemd, inclusief wat er dan met welk
 * werk gebeurt, zodat de keuze geïnformeerd is.
 */
import React from 'react';
import { Users } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { BuilderDraft, SitePage } from '../types';

interface ConflictDialogProps {
  open: boolean;
  localPages: SitePage[];
  serverDraft: BuilderDraft | null;
  onTakeServer: () => void;
  onKeepMine: () => void;
}

function describeDifference(localPages: SitePage[], serverPages: SitePage[]): string {
  const localById = new Map(localPages.map((page) => [page.pageId, page]));
  const serverById = new Map(serverPages.map((page) => [page.pageId, page]));

  const added = serverPages.filter((page) => !localById.has(page.pageId)).length;
  const removed = localPages.filter((page) => !serverById.has(page.pageId)).length;
  const changed = serverPages.filter((page) => {
    const mine = localById.get(page.pageId);
    return mine && JSON.stringify(mine) !== JSON.stringify(page);
  }).length;

  const parts: string[] = [];
  if (added) parts.push(`${added} nieuwe ${added === 1 ? 'pagina' : "pagina's"}`);
  if (removed) parts.push(`${removed} verwijderde ${removed === 1 ? 'pagina' : "pagina's"}`);
  if (changed) parts.push(`${changed} gewijzigde ${changed === 1 ? 'pagina' : "pagina's"}`);
  if (parts.length === 0) return 'De verschillen zijn klein.';
  return `Verschillen: ${parts.join(', ')}.`;
}

export function ConflictDialog({
  open,
  localPages,
  serverDraft,
  onTakeServer,
  onKeepMine,
}: ConflictDialogProps) {
  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-lg"
        // Geen sluitknop en geen sluiten-op-escape: doorwerken zonder keuze
        // zou betekenen dat er stil niets meer wordt opgeslagen.
        onEscapeKeyDown={(event: Event) => event.preventDefault()}
        onInteractOutside={(event: Event) => event.preventDefault()}
      >
        <DialogHeader>
          <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
            <Users className="h-5 w-5" />
          </div>
          <DialogTitle>Iemand anders heeft deze website gewijzigd</DialogTitle>
          <DialogDescription>
            Je wijzigingen zijn nog niet opgeslagen. Kies wat er moet gebeuren — er gaat pas iets
            weg als je hier kiest.
          </DialogDescription>
        </DialogHeader>

        <p className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-600">
          {serverDraft ? describeDifference(localPages, serverDraft.pages) : null}
        </p>

        <div className="mt-2 space-y-2.5">
          <button
            type="button"
            onClick={onKeepMine}
            data-testid="sites-conflict-keep-mine"
            className="w-full rounded-xl border border-slate-200 px-4 py-3.5 text-left transition hover:border-teal-300 hover:bg-teal-50/40"
          >
            <span className="block text-sm font-bold text-slate-900">Mijn versie aanhouden</span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Jouw wijzigingen worden opgeslagen bovenop die van de ander. De wijzigingen van de
              ander gaan dan verloren.
            </span>
          </button>
          <button
            type="button"
            onClick={onTakeServer}
            data-testid="sites-conflict-take-server"
            className="w-full rounded-xl border border-slate-200 px-4 py-3.5 text-left transition hover:border-slate-300 hover:bg-slate-50"
          >
            <span className="block text-sm font-bold text-slate-900">
              De versie van de ander laden
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Je begint verder met de actuele versie. Jouw niet-opgeslagen wijzigingen gaan dan
              verloren.
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
